/**
 * PhosphorIcons — set curado de @phosphor-icons/react para AetherLite.
 *
 * Phosphor da 6 weights por ícono (thin/light/regular/bold/fill/duotone)
 * que cubren desde "outline mínimo" hasta "lleno sólido". Acá curamos
 * ~200 íconos relevantes a fitness/coaching/nutrición/anatomía/UI; cada uno
 * se puede renderizar en CUALQUIER weight via el prop `weight` (default
 * "regular"). El picker tiene un toggle de weight global para que el coach
 * elija el estilo de TODOS los íconos a la vez.
 *
 * Storage en `PlanDecoration.icon` usa formato `"ph:<id>:<weight>"`
 * (ej. `"ph:dumbbell:fill"`). El dispatcher `LibraryIcon` en IconLibrary.tsx
 * detecta el prefijo `ph:` y rutea acá. Lucide (legacy) sigue funcionando
 * con ids sin prefijo (back-compat con decoraciones guardadas en sesión 7).
 *
 * Tree-shaking: Vite solo bundlea los componentes importados. Si en el
 * futuro vemos que el bundle pesa demasiado, se pueden eliminar entradas
 * sin afectar decoraciones existentes (graceful: render null).
 */
import {
  // === Ejercicio / Movimiento ===
  Barbell,
  Bicycle,
  Football,
  HandHeart,
  HandWaving,
  Heartbeat,
  Lightning,
  PersonSimple,
  PersonSimpleBike,
  PersonSimpleRun,
  PersonSimpleSwim,
  PersonSimpleTaiChi,
  PersonSimpleThrow,
  PersonSimpleWalk,
  Pulse,
  // === Anatomía / Cuerpo ===
  Bone,
  Brain,
  Eye,
  EyeSlash,
  Hand,
  HandFist,
  Heart,
  Tooth,
  // === Comida / Nutrición ===
  Bread,
  Carrot,
  Cheese,
  ChefHat,
  CookingPot,
  Cookie,
  EggCrack,
  Fish,
  ForkKnife,
  Hamburger,
  IceCream,
  Orange,
  Pepper,
  Pizza,
  Wine,
  // === Líquidos / Hidratación ===
  Coffee,
  Drop,
  Martini,
  Wine as WineGlass,
  // === Tiempo ===
  Alarm,
  Calendar,
  CalendarBlank,
  CalendarCheck,
  CalendarDots,
  Clock,
  ClockCountdown,
  Hourglass,
  HourglassHigh,
  HourglassLow,
  HourglassMedium,
  Timer,
  // === Métricas / Datos ===
  ChartBar,
  ChartLine,
  ChartLineUp,
  ChartPieSlice,
  Gauge,
  Percent,
  Ruler,
  Scales,
  Speedometer,
  TrendDown,
  TrendUp,
  // === Logros / Motivación ===
  Crown,
  CrownSimple,
  Medal,
  MedalMilitary,
  Rocket,
  Sparkle,
  Star,
  StarFour,
  Sword,
  Target,
  Trophy,
  // === Tiempo del día / Naturaleza ===
  Campfire,
  Cloud,
  CloudRain,
  CloudSun,
  Confetti,
  Flower,
  FlowerLotus,
  Leaf,
  Moon,
  MoonStars,
  Mountains,
  Plant,
  Snowflake,
  Sun,
  SunDim,
  SunHorizon,
  Tree,
  TreePalm,
  Waves,
  // === Bienestar / Estado ===
  Crosshair,
  Heart as HeartFavorite,
  HeartHalf,
  Smiley,
  SmileyMeh,
  SmileySad,
  SmileyXEyes,
  ThumbsDown,
  ThumbsUp,
  // === Música / Multimedia ===
  Headphones,
  MusicNote,
  MusicNotes,
  MusicNotesPlus,
  Play,
  // === Acciones / UI ===
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowsClockwise,
  ArrowUp,
  Check,
  Checks,
  CheckSquare,
  Circle,
  Cross,
  Hexagon,
  Plus,
  Square,
  Star as StarSimple,
  Triangle,
  X,
  XCircle,
  // === Salud / Médico ===
  Bandaids,
  FirstAidKit,
  Microscope,
  Pill,
  Stethoscope,
  Syringe,
  Thermometer,
  // === Equipo / Objetos ===
  AppleLogo,
  Backpack,
  Basket,
  Books,
  Camera,
  CameraPlus,
  Coins,
  CookingPot as Pot,
  CurrencyCircleDollar,
  Diamond,
  Flag,
  FlagBanner,
  Lightbulb,
  Lock,
  MapPin,
  Megaphone,
  Notepad,
  Package,
  PencilSimple,
  Quotes,
  Shield,
  ShieldCheck,
  Tag,
  Tent,
  TestTube,
  Trash,
  // === Texto / Identidad ===
  At,
  Briefcase,
  Buildings,
  Envelope,
  HandHeart as Hands,
  House,
  IdentificationBadge,
  Phone,
  Question,
  User,
  UserCircle,
  Users,
  WhatsappLogo,
  type Icon as PhosphorIconType,
} from "@phosphor-icons/react";

/** Categorías ordenadas para el picker. Mismas que IconLibrary (Lucide) más
 *  algunas variantes para que el menú combinado tenga sentido. */
export type PhosphorIconCategory =
  | "Ejercicio"
  | "Anatomía"
  | "Tiempo"
  | "Nutrición"
  | "Métricas"
  | "Logros"
  | "Naturaleza"
  | "Bienestar"
  | "UI / Acciones"
  | "Formas"
  | "Salud"
  | "Equipo gym"
  | "Identidad"
  | "Otros";

export type PhosphorWeight = "light" | "regular" | "bold" | "fill";

export interface PhosphorEntry {
  /** Sufijo del id Phosphor en storage. Se persiste como `ph:<id>:<weight>`. */
  id: string;
  /** Componente Phosphor (acepta `weight` prop). */
  Component: PhosphorIconType;
  /** Label legible para tooltip. */
  label: string;
  category: PhosphorIconCategory;
  tags: string[];
}

/**
 * 200+ íconos Phosphor curados. Para agregar uno nuevo:
 *   1. Importar el componente arriba (de @phosphor-icons/react)
 *   2. Agregar entrada acá con id (kebab-case), label, categoría, tags
 *   3. El picker los muestra automático con el weight global elegido
 */
export const PHOSPHOR_CATALOG: PhosphorEntry[] = [
  // === Ejercicio (15) ===
  { id: "barbell",            Component: Barbell,            label: "Barra de pesas",     category: "Ejercicio", tags: ["barra","pesa","gym","peso"] },
  { id: "bicycle",            Component: Bicycle,            label: "Bicicleta",          category: "Ejercicio", tags: ["bici","bicicleta","cardio"] },
  { id: "football",           Component: Football,           label: "Pelota",             category: "Ejercicio", tags: ["pelota","fútbol","balón","deporte"] },
  { id: "lightning",          Component: Lightning,          label: "Energía",            category: "Ejercicio", tags: ["energía","rayo","potencia","explosivo"] },
  { id: "heartbeat",          Component: Heartbeat,          label: "Latido",             category: "Ejercicio", tags: ["pulso","ritmo","corazón","cardio","ekg"] },
  { id: "person-simple",      Component: PersonSimple,       label: "Persona",            category: "Ejercicio", tags: ["persona","postura","cuerpo"] },
  { id: "person-bike",        Component: PersonSimpleBike,   label: "Persona en bici",    category: "Ejercicio", tags: ["bicicleta","ciclismo","persona"] },
  { id: "person-run",         Component: PersonSimpleRun,    label: "Persona corriendo",  category: "Ejercicio", tags: ["correr","running","cardio","sprint"] },
  { id: "person-swim",        Component: PersonSimpleSwim,   label: "Persona nadando",    category: "Ejercicio", tags: ["nadar","natación","piscina"] },
  { id: "person-taichi",      Component: PersonSimpleTaiChi, label: "Persona tai-chi",    category: "Ejercicio", tags: ["yoga","tai chi","estiramiento","balance"] },
  { id: "person-throw",       Component: PersonSimpleThrow,  label: "Persona lanzando",   category: "Ejercicio", tags: ["lanzar","throw","atleta"] },
  { id: "person-walk",        Component: PersonSimpleWalk,   label: "Persona caminando",  category: "Ejercicio", tags: ["caminar","walk","pasos"] },
  { id: "pulse",              Component: Pulse,              label: "Pulso",              category: "Ejercicio", tags: ["pulso","ritmo","ekg","frecuencia"] },
  { id: "hand-wave",          Component: HandWaving,         label: "Saludo",             category: "Ejercicio", tags: ["saludar","mano","bienvenida"] },

  // === Anatomía (9) ===
  { id: "bone",               Component: Bone,               label: "Hueso",              category: "Anatomía",  tags: ["hueso","esqueleto","calcio"] },
  { id: "brain",              Component: Brain,              label: "Cerebro",            category: "Anatomía",  tags: ["cerebro","mente","mental"] },
  { id: "eye",                Component: Eye,                label: "Ojo",                category: "Anatomía",  tags: ["ojo","visión","focus"] },
  { id: "eye-slash",          Component: EyeSlash,           label: "Ojo cerrado",        category: "Anatomía",  tags: ["ojo","ocultar","cerrado"] },
  { id: "hand",               Component: Hand,               label: "Mano",               category: "Anatomía",  tags: ["mano","grip","palma"] },
  { id: "hand-fist",          Component: HandFist,           label: "Puño",               category: "Anatomía",  tags: ["puño","fuerza","fist"] },
  { id: "heart",              Component: Heart,              label: "Corazón",            category: "Anatomía",  tags: ["corazón","amor","heart"] },
  { id: "tooth",              Component: Tooth,              label: "Diente",             category: "Anatomía",  tags: ["diente","dental","sonrisa"] },

  // === Comida / Nutrición (17) ===
  { id: "bread",              Component: Bread,              label: "Pan",                category: "Nutrición", tags: ["pan","carbo","desayuno"] },
  { id: "carrot",             Component: Carrot,             label: "Zanahoria",          category: "Nutrición", tags: ["zanahoria","verdura","vegetal"] },
  { id: "cheese",             Component: Cheese,             label: "Queso",              category: "Nutrición", tags: ["queso","lácteo","proteína"] },
  { id: "chef-hat",           Component: ChefHat,            label: "Gorro de chef",      category: "Nutrición", tags: ["chef","cocina","preparar"] },
  { id: "cooking-pot",        Component: CookingPot,         label: "Olla",               category: "Nutrición", tags: ["olla","cocina","caliente"] },
  { id: "cookie",             Component: Cookie,             label: "Galleta",            category: "Nutrición", tags: ["galleta","dulce","snack"] },
  { id: "egg-crack",          Component: EggCrack,           label: "Huevo",              category: "Nutrición", tags: ["huevo","proteína","desayuno"] },
  { id: "fish",               Component: Fish,               label: "Pescado",            category: "Nutrición", tags: ["pescado","omega","proteína"] },
  { id: "fork-knife",         Component: ForkKnife,          label: "Cubiertos",          category: "Nutrición", tags: ["comer","tenedor","cuchillo","comida"] },
  { id: "hamburger",          Component: Hamburger,          label: "Hamburguesa",        category: "Nutrición", tags: ["hamburguesa","burger","cheat","comida"] },
  { id: "ice-cream",          Component: IceCream,           label: "Helado",             category: "Nutrición", tags: ["helado","postre","dulce","cheat"] },
  { id: "orange",             Component: Orange,             label: "Naranja",            category: "Nutrición", tags: ["naranja","cítrico","fruta","vitamina c"] },
  { id: "pepper",             Component: Pepper,             label: "Pimiento",           category: "Nutrición", tags: ["pimiento","ají","verdura"] },
  { id: "pizza",              Component: Pizza,              label: "Pizza",              category: "Nutrición", tags: ["pizza","comida","cheat"] },
  { id: "hand-heart",         Component: HandHeart,          label: "Mano con corazón",   category: "Nutrición", tags: ["amor","cuidar","wellness","gratitud"] },
  { id: "first-aid-kit",      Component: FirstAidKit,        label: "Botiquín",           category: "Salud",     tags: ["botiquín","primeros auxilios","kit"] },

  // === Líquidos / Hidratación (4) ===
  { id: "coffee",             Component: Coffee,             label: "Café",               category: "Nutrición", tags: ["café","cafeína","pre-entreno"] },
  { id: "drop",               Component: Drop,               label: "Gota",               category: "Nutrición", tags: ["agua","gota","hidratación"] },
  { id: "martini",            Component: Martini,            label: "Cóctel",             category: "Nutrición", tags: ["cóctel","martini","alcohol","cheat"] },
  { id: "wine-glass",         Component: WineGlass,          label: "Copa de vino",       category: "Nutrición", tags: ["vino","copa","alcohol"] },

  // === Tiempo (13) ===
  { id: "alarm",              Component: Alarm,              label: "Alarma",             category: "Tiempo",    tags: ["alarma","despertador","alerta"] },
  { id: "calendar",           Component: Calendar,           label: "Calendario",         category: "Tiempo",    tags: ["calendario","fecha","mes"] },
  { id: "calendar-blank",     Component: CalendarBlank,      label: "Calendario vacío",   category: "Tiempo",    tags: ["calendario","blank","fecha"] },
  { id: "calendar-check",     Component: CalendarCheck,      label: "Día completado",     category: "Tiempo",    tags: ["check","fecha","completado"] },
  { id: "calendar-dots",      Component: CalendarDots,       label: "Calendario eventos", category: "Tiempo",    tags: ["calendario","eventos","puntos"] },
  { id: "clock",              Component: Clock,              label: "Reloj",              category: "Tiempo",    tags: ["reloj","hora","tiempo"] },
  { id: "clock-countdown",    Component: ClockCountdown,     label: "Cuenta regresiva",   category: "Tiempo",    tags: ["countdown","cuenta atrás","timer"] },
  { id: "hourglass",          Component: Hourglass,          label: "Reloj de arena",     category: "Tiempo",    tags: ["arena","tiempo","hourglass"] },
  { id: "hourglass-high",     Component: HourglassHigh,      label: "Arena lleno",        category: "Tiempo",    tags: ["arena","comienzo","tiempo"] },
  { id: "hourglass-medium",   Component: HourglassMedium,    label: "Arena medio",        category: "Tiempo",    tags: ["arena","medio","mitad"] },
  { id: "hourglass-low",      Component: HourglassLow,       label: "Arena bajo",         category: "Tiempo",    tags: ["arena","poco","final"] },
  { id: "timer",              Component: Timer,              label: "Cronómetro",         category: "Tiempo",    tags: ["timer","cronómetro","tiempo"] },
  { id: "arrows-clockwise",   Component: ArrowsClockwise,    label: "Repetir",            category: "Tiempo",    tags: ["repetir","ciclo","reps","series","loop"] },

  // === Métricas (11) ===
  { id: "chart-bar",          Component: ChartBar,           label: "Barras",             category: "Métricas",  tags: ["barras","gráfico","stats"] },
  { id: "chart-line",         Component: ChartLine,          label: "Línea",              category: "Métricas",  tags: ["línea","gráfico","tendencia"] },
  { id: "chart-line-up",      Component: ChartLineUp,        label: "Línea subiendo",     category: "Métricas",  tags: ["progreso","subir","mejorar"] },
  { id: "chart-pie",          Component: ChartPieSlice,      label: "Torta",              category: "Métricas",  tags: ["torta","porcentaje","stats"] },
  { id: "gauge",              Component: Gauge,              label: "Medidor",            category: "Métricas",  tags: ["medidor","gauge","intensidad"] },
  { id: "percent",            Component: Percent,            label: "Porcentaje",         category: "Métricas",  tags: ["porcentaje","%","grasa","masa"] },
  { id: "ruler",              Component: Ruler,              label: "Regla",              category: "Métricas",  tags: ["regla","medida","cm"] },
  { id: "scales",             Component: Scales,             label: "Balanza",            category: "Métricas",  tags: ["balanza","peso","kg","báscula"] },
  { id: "speedometer",        Component: Speedometer,        label: "Velocímetro",        category: "Métricas",  tags: ["velocidad","speed","intensidad"] },
  { id: "trend-up",           Component: TrendUp,            label: "Tendencia ↑",        category: "Métricas",  tags: ["subir","ganar","mejora","up"] },
  { id: "trend-down",         Component: TrendDown,          label: "Tendencia ↓",        category: "Métricas",  tags: ["bajar","perder","grasa","déficit","down"] },

  // === Logros (11) ===
  { id: "crown",              Component: Crown,              label: "Corona",             category: "Logros",    tags: ["corona","rey","premium","élite"] },
  { id: "crown-simple",       Component: CrownSimple,        label: "Corona simple",      category: "Logros",    tags: ["corona","reina","top"] },
  { id: "medal",              Component: Medal,              label: "Medalla",            category: "Logros",    tags: ["medalla","podio","logro"] },
  { id: "medal-military",     Component: MedalMilitary,      label: "Medalla militar",    category: "Logros",    tags: ["medalla","honor","logro"] },
  { id: "rocket",             Component: Rocket,             label: "Cohete",             category: "Logros",    tags: ["cohete","launch","despegar","rápido"] },
  { id: "sparkle",            Component: Sparkle,            label: "Destello",           category: "Logros",    tags: ["destello","brillo","premium"] },
  { id: "star",               Component: Star,               label: "Estrella",           category: "Logros",    tags: ["estrella","destacado","favorito"] },
  { id: "star-four",          Component: StarFour,           label: "Estrella 4 puntas",  category: "Logros",    tags: ["estrella","brillo","spark"] },
  { id: "sword",              Component: Sword,              label: "Espada",             category: "Logros",    tags: ["espada","guerrero","ataque"] },
  { id: "target",             Component: Target,             label: "Diana",              category: "Logros",    tags: ["objetivo","meta","target","goal"] },
  { id: "trophy",             Component: Trophy,             label: "Trofeo",             category: "Logros",    tags: ["trofeo","ganador","logro","premio"] },

  // === Naturaleza / Clima (16) ===
  { id: "campfire",           Component: Campfire,           label: "Fogata",             category: "Naturaleza", tags: ["fogata","fuego","outdoor"] },
  { id: "cloud",              Component: Cloud,              label: "Nube",               category: "Naturaleza", tags: ["nube","clima","weather"] },
  { id: "cloud-rain",         Component: CloudRain,          label: "Lluvia",             category: "Naturaleza", tags: ["lluvia","clima","mojado"] },
  { id: "cloud-sun",          Component: CloudSun,           label: "Sol con nube",       category: "Naturaleza", tags: ["clima","nublado","sol"] },
  { id: "confetti",           Component: Confetti,           label: "Confeti",            category: "Naturaleza", tags: ["confeti","celebración","fiesta"] },
  { id: "flower",             Component: Flower,             label: "Flor",               category: "Naturaleza", tags: ["flor","natural","planta"] },
  { id: "flower-lotus",       Component: FlowerLotus,        label: "Loto",               category: "Naturaleza", tags: ["loto","yoga","meditación","wellness"] },
  { id: "leaf",               Component: Leaf,               label: "Hoja",               category: "Naturaleza", tags: ["hoja","natural","verde","planta"] },
  { id: "moon",               Component: Moon,               label: "Luna",               category: "Naturaleza", tags: ["luna","noche","descanso"] },
  { id: "moon-stars",         Component: MoonStars,          label: "Luna y estrellas",   category: "Naturaleza", tags: ["luna","noche","sueño"] },
  { id: "mountains",          Component: Mountains,          label: "Montañas",           category: "Naturaleza", tags: ["montaña","outdoor","hiking"] },
  { id: "plant",              Component: Plant,              label: "Planta",             category: "Naturaleza", tags: ["planta","verde","natural"] },
  { id: "snowflake",          Component: Snowflake,          label: "Copo de nieve",      category: "Naturaleza", tags: ["nieve","invierno","frío"] },
  { id: "sun",                Component: Sun,                label: "Sol",                category: "Naturaleza", tags: ["sol","día","mañana"] },
  { id: "sun-dim",            Component: SunDim,             label: "Sol tenue",          category: "Naturaleza", tags: ["sol","claro","luz tenue"] },
  { id: "sun-horizon",        Component: SunHorizon,         label: "Sol al horizonte",   category: "Naturaleza", tags: ["amanecer","atardecer","sol"] },
  { id: "tree",               Component: Tree,               label: "Árbol",              category: "Naturaleza", tags: ["árbol","outdoor","natural"] },
  { id: "tree-palm",          Component: TreePalm,           label: "Palmera",            category: "Naturaleza", tags: ["palmera","playa","tropical"] },
  { id: "waves",              Component: Waves,              label: "Olas",               category: "Naturaleza", tags: ["agua","mar","natación","olas"] },

  // === Bienestar / Emociones (10) ===
  { id: "crosshair",          Component: Crosshair,          label: "Punto de mira",      category: "Bienestar", tags: ["focus","objetivo","target","mira"] },
  { id: "heart-favorite",     Component: HeartFavorite,      label: "Corazón",            category: "Bienestar", tags: ["amor","favorito","love"] },
  { id: "heart-half",         Component: HeartHalf,          label: "Medio corazón",      category: "Bienestar", tags: ["amor","mitad","parcial"] },
  { id: "smiley",             Component: Smiley,             label: "Sonrisa",            category: "Bienestar", tags: ["feliz","sonrisa","positivo"] },
  { id: "smiley-meh",         Component: SmileyMeh,          label: "Neutral",            category: "Bienestar", tags: ["neutral","meh","regular"] },
  { id: "smiley-sad",         Component: SmileySad,          label: "Triste",             category: "Bienestar", tags: ["triste","sad","negativo"] },
  { id: "smiley-x-eyes",      Component: SmileyXEyes,        label: "Agotado",            category: "Bienestar", tags: ["cansado","agotado","fatiga"] },
  { id: "thumbs-up",          Component: ThumbsUp,           label: "Pulgar arriba",      category: "Bienestar", tags: ["ok","bien","aprobado","like"] },
  { id: "thumbs-down",        Component: ThumbsDown,         label: "Pulgar abajo",       category: "Bienestar", tags: ["mal","no","dislike"] },
  { id: "hands",              Component: Hands,              label: "Manos",              category: "Bienestar", tags: ["manos","apoyo","gratitud"] },

  // === Música / Multimedia (5) ===
  { id: "headphones",         Component: Headphones,         label: "Auriculares",        category: "Bienestar", tags: ["auriculares","música","cardio"] },
  { id: "music-note",         Component: MusicNote,          label: "Nota musical",       category: "Bienestar", tags: ["música","nota","sonido"] },
  { id: "music-notes",        Component: MusicNotes,         label: "Notas musicales",    category: "Bienestar", tags: ["música","playlist","entreno"] },
  { id: "music-notes-plus",   Component: MusicNotesPlus,     label: "Agregar música",     category: "Bienestar", tags: ["música","agregar","playlist"] },
  { id: "play",               Component: Play,               label: "Play",               category: "UI / Acciones", tags: ["play","iniciar"] },

  // === UI / Acciones (13) ===
  { id: "arrow-up",           Component: ArrowUp,            label: "Subir",              category: "UI / Acciones", tags: ["flecha","arriba","subir"] },
  { id: "arrow-down",         Component: ArrowDown,          label: "Bajar",              category: "UI / Acciones", tags: ["flecha","abajo","bajar"] },
  { id: "arrow-left",         Component: ArrowLeft,          label: "Izquierda",          category: "UI / Acciones", tags: ["flecha","izquierda","atrás"] },
  { id: "arrow-right",        Component: ArrowRight,         label: "Derecha",            category: "UI / Acciones", tags: ["flecha","derecha","siguiente"] },
  { id: "check",              Component: Check,              label: "Check",              category: "UI / Acciones", tags: ["check","hecho","ok"] },
  { id: "checks",             Component: Checks,             label: "Doble check",        category: "UI / Acciones", tags: ["doble check","completo","verificado"] },
  { id: "check-square",       Component: CheckSquare,        label: "Checkbox marcado",   category: "UI / Acciones", tags: ["checkbox","hecho","task"] },
  { id: "plus",               Component: Plus,               label: "Más",                category: "UI / Acciones", tags: ["más","plus","agregar"] },
  { id: "x",                  Component: X,                  label: "Cerrar",             category: "UI / Acciones", tags: ["x","cerrar","cancelar"] },
  { id: "x-circle",           Component: XCircle,            label: "X en círculo",       category: "UI / Acciones", tags: ["x","cancelar","negar"] },
  { id: "cross",              Component: Cross,              label: "Cruz",               category: "UI / Acciones", tags: ["cruz","más","ayuda","cruz roja"] },
  { id: "circle",             Component: Circle,             label: "Círculo",            category: "Formas",    tags: ["círculo","forma","redondo"] },
  { id: "square",             Component: Square,             label: "Cuadrado",           category: "Formas",    tags: ["cuadrado","forma"] },
  { id: "triangle",           Component: Triangle,           label: "Triángulo",          category: "Formas",    tags: ["triángulo","forma"] },
  { id: "hexagon",            Component: Hexagon,            label: "Hexágono",           category: "Formas",    tags: ["hexágono","forma"] },
  { id: "diamond",            Component: Diamond,            label: "Diamante",           category: "Formas",    tags: ["diamante","rombo","forma"] },
  { id: "star-simple",        Component: StarSimple,         label: "Estrella simple",    category: "Formas",    tags: ["estrella","forma"] },

  // === Salud / Médico (6) ===
  { id: "bandaids",           Component: Bandaids,           label: "Curitas",            category: "Salud",     tags: ["curitas","lesión","herida"] },
  { id: "microscope",         Component: Microscope,         label: "Microscopio",        category: "Salud",     tags: ["microscopio","ciencia","análisis"] },
  { id: "pill",               Component: Pill,               label: "Pastilla",           category: "Salud",     tags: ["pastilla","medicamento","suplemento"] },
  { id: "stethoscope",        Component: Stethoscope,        label: "Estetoscopio",       category: "Salud",     tags: ["estetoscopio","médico","doctor"] },
  { id: "syringe",            Component: Syringe,            label: "Jeringa",            category: "Salud",     tags: ["jeringa","vacuna","inyección"] },
  { id: "thermometer",        Component: Thermometer,        label: "Termómetro",         category: "Salud",     tags: ["termómetro","temperatura","fiebre"] },
  { id: "test-tube",          Component: TestTube,           label: "Tubo de ensayo",     category: "Salud",     tags: ["laboratorio","ciencia","tubo"] },

  // === Equipo / Objetos (16) ===
  { id: "apple-logo",         Component: AppleLogo,          label: "Manzana logo",       category: "Otros",     tags: ["apple","logo","fruta"] },
  { id: "backpack",           Component: Backpack,           label: "Mochila",            category: "Otros",     tags: ["mochila","gym","bag"] },
  { id: "basket",             Component: Basket,             label: "Cesta",              category: "Otros",     tags: ["cesta","compras","mercado"] },
  { id: "books",              Component: Books,              label: "Libros",             category: "Otros",     tags: ["libros","leer","conocimiento"] },
  { id: "briefcase",          Component: Briefcase,          label: "Maletín",            category: "Identidad", tags: ["maletín","trabajo","profesional"] },
  { id: "buildings",          Component: Buildings,          label: "Edificios",          category: "Identidad", tags: ["edificios","gimnasio","ciudad"] },
  { id: "camera",             Component: Camera,             label: "Cámara",             category: "Otros",     tags: ["cámara","foto","registro"] },
  { id: "camera-plus",        Component: CameraPlus,         label: "Agregar foto",       category: "Otros",     tags: ["cámara","agregar","foto"] },
  { id: "coins",              Component: Coins,              label: "Monedas",            category: "Otros",     tags: ["monedas","dinero","ahorro"] },
  { id: "currency-dollar",    Component: CurrencyCircleDollar, label: "Dólar",            category: "Otros",     tags: ["dólar","precio","dinero"] },
  { id: "flag",               Component: Flag,               label: "Bandera",            category: "Otros",     tags: ["bandera","meta","goal"] },
  { id: "flag-banner",        Component: FlagBanner,         label: "Estandarte",         category: "Otros",     tags: ["bandera","estandarte","triunfo"] },
  { id: "lightbulb",          Component: Lightbulb,          label: "Bombilla",           category: "Otros",     tags: ["idea","luz","tip"] },
  { id: "lock",               Component: Lock,               label: "Candado",            category: "Otros",     tags: ["candado","privado","seguro"] },
  { id: "map-pin",            Component: MapPin,             label: "Pin de mapa",        category: "Otros",     tags: ["ubicación","mapa","lugar"] },
  { id: "megaphone",          Component: Megaphone,          label: "Megáfono",           category: "Otros",     tags: ["megáfono","anuncio","alerta"] },
  { id: "notepad",            Component: Notepad,            label: "Bloc",               category: "Otros",     tags: ["bloc","notas","apuntes"] },
  { id: "package",            Component: Package,            label: "Paquete",            category: "Otros",     tags: ["paquete","caja","envío"] },
  { id: "pencil",             Component: PencilSimple,       label: "Lápiz",              category: "Otros",     tags: ["lápiz","editar","escribir"] },
  { id: "quotes",             Component: Quotes,             label: "Comillas",           category: "Otros",     tags: ["cita","frase","comillas"] },
  { id: "shield",             Component: Shield,             label: "Escudo",             category: "Otros",     tags: ["escudo","proteger","defensa"] },
  { id: "shield-check",       Component: ShieldCheck,        label: "Escudo verificado",  category: "Otros",     tags: ["escudo","verificado","aprobado"] },
  { id: "tag",                Component: Tag,                label: "Etiqueta",           category: "Otros",     tags: ["etiqueta","tag","label"] },
  { id: "tent",               Component: Tent,               label: "Carpa",              category: "Otros",     tags: ["carpa","camping","outdoor"] },
  { id: "trash",              Component: Trash,              label: "Basura",             category: "Otros",     tags: ["basura","eliminar","trash"] },

  // === Identidad / Contacto (10) ===
  { id: "at",                 Component: At,                 label: "Arroba",             category: "Identidad", tags: ["arroba","email","@"] },
  { id: "envelope",           Component: Envelope,           label: "Sobre",              category: "Identidad", tags: ["sobre","email","mensaje"] },
  { id: "house",              Component: House,              label: "Casa",               category: "Identidad", tags: ["casa","home","dirección"] },
  { id: "id-badge",           Component: IdentificationBadge, label: "Credencial",        category: "Identidad", tags: ["credencial","id","badge"] },
  { id: "phone",              Component: Phone,              label: "Teléfono",           category: "Identidad", tags: ["teléfono","llamar","phone"] },
  { id: "question",           Component: Question,           label: "Pregunta",           category: "UI / Acciones", tags: ["pregunta","ayuda","?"] },
  { id: "user",               Component: User,               label: "Usuario",            category: "Identidad", tags: ["usuario","perfil","persona"] },
  { id: "user-circle",        Component: UserCircle,         label: "Avatar",             category: "Identidad", tags: ["avatar","perfil","usuario"] },
  { id: "users",              Component: Users,              label: "Usuarios",           category: "Identidad", tags: ["usuarios","grupo","equipo"] },
  { id: "whatsapp",           Component: WhatsappLogo,       label: "WhatsApp",           category: "Identidad", tags: ["whatsapp","contacto","mensajería"] },
];

/** Mapa rápido por id. */
const PHOSPHOR_MAP: Map<string, PhosphorEntry> = new Map(PHOSPHOR_CATALOG.map((e) => [e.id, e]));

/** Lookup por id base (sin prefijo `ph:` ni weight). */
export function getPhosphorEntry(id: string): PhosphorEntry | undefined {
  return PHOSPHOR_MAP.get(id);
}

/** Categorías en el orden que aparecen en el picker. */
export const PHOSPHOR_CATEGORIES: PhosphorIconCategory[] = [
  "Ejercicio",
  "Anatomía",
  "Tiempo",
  "Nutrición",
  "Métricas",
  "Logros",
  "Naturaleza",
  "Bienestar",
  "UI / Acciones",
  "Formas",
  "Salud",
  "Identidad",
  "Otros",
];

/** Búsqueda con tokens (igual que searchIcons de IconLibrary). */
export function searchPhosphor(query: string): PhosphorEntry[] {
  const q = (query || "").trim().toLowerCase();
  if (!q) return PHOSPHOR_CATALOG;
  const tokens = q.split(/\s+/).filter(Boolean);
  return PHOSPHOR_CATALOG.filter((e) => {
    const haystack = `${e.label.toLowerCase()} ${e.tags.join(" ")} ${e.id}`;
    return tokens.every((t) => haystack.includes(t));
  });
}

/** Weights disponibles. Phosphor también soporta "thin" y "duotone"; los
 *  dejamos fuera del picker para no abrumar al coach (cuatro estilos
 *  cubren el 99% de los casos). */
export const PHOSPHOR_WEIGHTS: Array<{ id: PhosphorWeight; label: string }> = [
  { id: "light",   label: "Fino" },
  { id: "regular", label: "Normal" },
  { id: "bold",    label: "Grueso" },
  { id: "fill",    label: "Lleno" },
];
